/**
 * Product variant metafield gallery (custom.variant_img + custom.imagenes_talla combined) + product.media fallback.
 * Patches product-media after CE upgrade; polls form variant id to force metafield swaps (reliable).
 */
(function () {
  const LOG = '[featured-variant-gallery]';

  function parseJson(id) {
    const el = document.getElementById(id);
    if (!el) {
      console.warn(LOG, 'No existe el nodo JSON con id:', id);
      return null;
    }
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.warn(LOG, 'JSON inválido:', id, e);
      return null;
    }
  }

  function escapeHtml(s) {
    const d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  }

  function slideHtml(sectionId, variantId, index0, img, mediaSize, aspectDesktop, aspectMobile, isFirst, seoAlt) {
    const sid = String(sectionId);
    const vid = String(variantId);
    const idx = index0 + 1;
    const mediaId = `mf-${vid}-${index0}`;
    const domId = `product-${sid}-mf-${vid}-${idx}`;
    const loading = isFirst ? 'eager' : 'lazy';
    const sel = isFirst ? 'is-initial-selected is-selected' : '';
    const alt = seoAlt || img.alt || '';
    const ar = img.aspectRatio || 1;
    const ratioCss = aspectDesktop && String(aspectDesktop).trim() !== '' ? aspectDesktop : ar;
    const mobileStyle =
      aspectMobile && aspectMobile !== '' && aspectMobile !== aspectDesktop
        ? `@media screen and (max-width: 999px) { #${domId}{ --aspect-ratio: ${aspectMobile}; } }`
        : '';

    return (
      `<div id="${domId}" class="product__media-item ${sel}" data-media-type="image" data-media-id="${mediaId}" data-original-position="${index0}">` +
      `<div class="product__media-image-wrapper aspect-ratio aspect-ratio--natural aspect-ratio--${mediaSize}" style="padding-bottom: calc(100% / (var(--aspect-ratio)));">` +
      `<img src="${escapeHtml(img.src)}" alt="${escapeHtml(alt)}" loading="${loading}" width="${img.width || ''}" height="${img.height || ''}" decoding="async" />` +
      `</div>` +
      `<style>#${domId}{ --aspect-ratio: ${ratioCss}; }${mobileStyle}</style>` +
      `</div>`
    );
  }

  function thumbsHtml(sectionId, variantId, images, productTitle) {
    let out = '';
    images.forEach((img, index0) => {
      const vid = String(variantId);
      const domId = `product-${sectionId}-mf-${vid}-${index0 + 1}`;
      const thumbSrc = img.thumbSrc || img.src;
      const alt = img.alt || productTitle || '';
      const cur = index0 === 0 ? ' aria-current="true"' : '';
      out +=
        `<button type="button" tabindex="-1" class="product__thumbnail-item" aria-controls="${domId}" data-media-id="mf-${vid}-${index0}" aria-label="${escapeHtml(alt)}" data-action="select"${cur}>` +
        `<div class="product__thumbnail"><img src="${escapeHtml(thumbSrc)}" alt="${escapeHtml(alt)}" loading="lazy" sizes="(max-width: 999px) 72px, 60px" width="72" height="72" /></div>` +
        `</button>`;
    });
    return out;
  }

  function dotsHtml(count, sectionId, variantId) {
    let out = '';
    for (let i = 0; i < count; i++) {
      const domId = `product-${sectionId}-mf-${variantId}-${i + 1}`;
      const cur = i === 0 ? ' aria-current="true"' : '';
      out +=
        `<button type="button" tabindex="-1" class="dots-nav__item tap-area"${cur} aria-controls="${domId}" data-media-id="mf-${variantId}-${i}" data-action="select">` +
        `<span class="visually-hidden">${i + 1}</span></button>`;
    }
    return out;
  }

  function imageUrlWithWidth(src, width) {
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (e) {
      return src;
    }
  }

  function metafieldImageFromSlide(cfg, slide) {
    const parts = /^mf-(\d+)-(\d+)$/.exec(slide.getAttribute('data-media-id') || '');
    if (!parts) return null;
    const vcfg = cfg.variants[parts[1]];
    return (vcfg && vcfg.images && vcfg.images[Number(parts[2])]) || null;
  }

  function getGalleryMode(pm) {
    const firstItem = pm.querySelector('flickity-carousel .product__media-item');
    if (!firstItem) return 'unknown';
    const mediaId = firstItem.getAttribute('data-media-id') || '';
    return mediaId.startsWith('mf-') ? 'metafield' : 'fallback';
  }

  function isShowingMetafieldGallery(pm, variantId) {
    if (getGalleryMode(pm) !== 'metafield') return false;
    const firstItem = pm.querySelector('flickity-carousel .product__media-item');
    return firstItem?.getAttribute('data-media-id') === `mf-${variantId}-0`;
  }

  const STILL_PENDING = Symbol('flickity-pending');

  /**
   * En el layout "gallery" de escritorio el tema muestra las imágenes en rejilla y destruye
   * Flickity, así que ahí no hay que volver a montarlo tras cambiar los slides.
   */
  function usesFlickity(carousel) {
    if (carousel.getAttribute('desktop-media-layout') !== 'gallery') return true;
    const lapAndUp = window.themeVariables?.breakpoints?.lapAndUp;
    return lapAndUp ? !window.matchMedia(lapAndUp).matches : true;
  }

  /**
   * Flickity mueve los slides dentro de un .flickity-viewport propio, así que hay que
   * desactivarlo con el DOM todavía intacto: su deactivate() hace removeChild(viewport) y
   * lanza si el HTML ya se ha reemplazado, dejando la galería sin instancia (primera imagen
   * fija y thumbnails/dots muertos).
   */
  async function swapCarouselSlides(carousel, html) {
    if (!carousel) return;

    const creation = carousel.flickityInstance;
    let instance = null;

    if (creation) {
      let timer;
      try {
        instance = await Promise.race([
          creation,
          new Promise((resolve) => {
            timer = setTimeout(() => resolve(STILL_PENDING), 800);
          }),
        ]);
      } catch (e) {
        instance = null;
      } finally {
        clearTimeout(timer);
      }

      if (instance === STILL_PENDING) {
        // Flickity aún se está montando: recibirá los slides nuevos al terminar.
        carousel.innerHTML = html;
        return;
      }
    }

    if (instance && instance.isActive) {
      try {
        instance.destroy();
      } catch (e) {
        console.warn(LOG, 'No se pudo destruir la instancia de Flickity', e);
      }
    }

    carousel.innerHTML = html;

    if (!usesFlickity(carousel)) return;

    if (typeof carousel._createFlickity === 'function') {
      carousel._createFlickity();
    } else {
      console.warn(LOG, 'flickity-carousel sin _createFlickity: el carrusel quedará estático');
    }
  }

  /**
   * product-media cachea los slides en connectedCallback para abrir el zoom al pulsar la
   * imagen (layout gallery), así que tras sustituirlos hay que reapuntar la caché.
   */
  function refreshMediaItems(pm) {
    const items = pm.querySelectorAll('.product__media-item[data-media-type="image"]');

    if (pm.bindedOnClickImage) {
      if (pm.imagesItems) {
        Array.from(pm.imagesItems).forEach((item) => item.removeEventListener('click', pm.bindedOnClickImage));
      }
      items.forEach((item) => item.addEventListener('click', pm.bindedOnClickImage));
    }

    pm.imagesItems = items;
  }

  async function replaceMetafieldGallery(pm, cfg, variantId) {
    const v = cfg.variants[String(variantId)];
    if (!v || !v.images || !v.images.length) {
      return false;
    }

    const carousel = pm.querySelector('flickity-carousel');
    if (!carousel) return false;
    const controls = pm.querySelector('flickity-controls');

    const sectionId = cfg.sectionId;
    const mediaSize = pm.dataset.mediaSize || 'natural';
    const aspectDesktop = pm.dataset.mediaAspect || '';
    const aspectMobile = pm.dataset.mobileMediaAspect || '';

    let slides = '';
    v.images.forEach((img, i) => {
      slides += slideHtml(sectionId, variantId, i, img, mediaSize, aspectDesktop, aspectMobile, i === 0, img.alt);
    });

    // Los controles se renuevan antes del swap: al montar Flickity se emite flickity:ready y
    // flickity-controls marca como activo el thumbnail que apunta al slide seleccionado.
    if (controls) {
      const thumbInner = controls.querySelector('.product__thumbnail-list-inner');
      if (thumbInner) thumbInner.innerHTML = thumbsHtml(sectionId, variantId, v.images, cfg.productTitle || '');

      const dotsWrap = controls.querySelector('.dots-nav');
      if (dotsWrap && v.images.length > 1) {
        dotsWrap.innerHTML = dotsHtml(v.images.length, sectionId, variantId);
      } else if (dotsWrap && v.images.length <= 1) {
        dotsWrap.innerHTML = '';
      }

      controls.hidden = v.images.length <= 1;
    }

    const zoomBtn = pm.querySelector('.product__zoom-button');
    if (zoomBtn) zoomBtn.hidden = false;

    await swapCarouselSlides(carousel, slides);
    refreshMediaItems(pm);
    return true;
  }

  async function replaceFallbackGallery(pm, cfg, source) {
    const sid = cfg.sectionId;
    const slidesTpl = document.getElementById(`featured-variant-fallback-slides-${sid}`);
    const thumbsTpl = document.getElementById(`featured-variant-fallback-thumbs-${sid}`);
    const dotsTpl = document.getElementById(`featured-variant-fallback-dots-${sid}`);
    if (!slidesTpl) {
      console.warn(LOG, 'replaceFallbackGallery: no hay template #featured-variant-fallback-slides-' + sid, { source });
      return false;
    }

    const carousel = pm.querySelector('flickity-carousel');
    if (!carousel) return false;
    const controls = pm.querySelector('flickity-controls');

    if (controls) {
      const thumbInner = controls.querySelector('.product__thumbnail-list-inner');
      if (thumbInner && thumbsTpl) thumbInner.innerHTML = thumbsTpl.innerHTML;

      const dotsWrap = controls.querySelector('.dots-nav');
      if (dotsWrap && dotsTpl) dotsWrap.innerHTML = dotsTpl.innerHTML;

      const slideCount = slidesTpl.content.querySelectorAll('.product__media-item').length;
      controls.hidden = slideCount <= 1;
    }

    await swapCarouselSlides(carousel, slidesTpl.innerHTML);
    refreshMediaItems(pm);
    return true;
  }

  async function selectFallbackSlide(pm, featuredMediaId) {
    if (featuredMediaId == null) return;
    const carousel = pm.querySelector('flickity-carousel');
    if (!carousel || typeof carousel.select !== 'function') return;
    try {
      await carousel.select(`[data-media-id="${featuredMediaId}"]`);
    } catch (e) {
      /* ignore */
    }
  }

  /**
   * El cambio de variante llega por dos vías, el sondeo del formulario y el evento del tema,
   * y ambas pueden empezar a sustituir la galería a la vez: una dejaría a Flickity montado
   * sobre slides que la otra ya ha quitado. Las sustituciones se encadenan.
   */
  function queueGallerySwap(pm, task) {
    const pending = pm.variantGalleryQueue || Promise.resolve();
    const next = pending.then(task, task);
    pm.variantGalleryQueue = next.catch(() => {});
    return next;
  }

  /**
   * Poll variant input: only swaps to metafield slides (theme handles product.media via patched handler).
   */
  function bindMetafieldPoll(pm, cfg) {
    const formId = pm.getAttribute('form-id');
    const form = formId ? document.getElementById(formId) : null;
    if (!form) {
      console.warn(LOG, 'bindMetafieldPoll: no se encontró el formulario', { formId });
      return;
    }
    if (!cfg) return;

    const getVariantInput = () => form.querySelector('input[name="id"]');
    // Sin talla preseleccionada el input queda vacío: hay que registrar ese estado para que
    // elegir la primera talla cuente como cambio aunque coincida con la variante por defecto.
    let lastId = getVariantInput()?.value || null;

    async function maybeMetafield(rawId) {
      const id = rawId || null;
      if (id === lastId) return;
      lastId = id;
      if (!id) return;

      const vcfg = cfg.variants[String(id)];

      if (vcfg && vcfg.hasMetafield && vcfg.images && vcfg.images.length > 0) {
        await queueGallerySwap(pm, async () => {
          if (isShowingMetafieldGallery(pm, id)) return;
          await replaceMetafieldGallery(pm, cfg, id);
        });
      }
    }

    form.addEventListener('change', () => maybeMetafield(getVariantInput()?.value));
    form.addEventListener('input', () => maybeMetafield(getVariantInput()?.value));

    setInterval(() => {
      maybeMetafield(getVariantInput()?.value);
    }, 250);
  }

  function patchOnVariantChanged(pm, cfg) {
    if (pm.dataset.variantGalleryOvInstalled) return;
    pm.dataset.variantGalleryOvInstalled = 'true';

    const native =
      typeof pm.onVariantChanged === 'function' ? pm.onVariantChanged.bind(pm) : null;

    if (!native) {
      console.warn(LOG, 'patchOnVariantChanged: pm.onVariantChanged no es función (¿CE no actualizado?)');
    }

    pm.onVariantChanged = async function (payload) {
      const variant = payload && payload.variant;
      const variantIdStr = variant && variant.id != null ? String(variant.id) : null;

      if (!variantIdStr || !cfg || !cfg.variants) {
        if (native) return native(payload);
        return;
      }

      const vcfg = cfg.variants[variantIdStr];

      if (vcfg && vcfg.hasMetafield && vcfg.images && vcfg.images.length > 0) {
        await queueGallerySwap(pm, async () => {
          if (isShowingMetafieldGallery(pm, variantIdStr)) return;
          await replaceMetafieldGallery(pm, cfg, variantIdStr);
        });
        return;
      }

      // Only restore product.media slides when leaving a metafield gallery.
      // For normal variants the theme handler filters/selects slides without replacing DOM.
      await queueGallerySwap(pm, async () => {
        if (getGalleryMode(pm) !== 'metafield') return;
        await replaceFallbackGallery(pm, cfg, 'onVariantChanged');
      });

      if (native) return native(payload);
      if (vcfg && vcfg.featuredMediaId != null) await selectFallbackSlide(pm, vcfg.featuredMediaId);
    };
  }

  /**
   * El zoom del tema resuelve cada slide contra product.media buscando por data-media-id
   * numérico. Las imágenes de talla son ficheros, no media del producto, así que su id
   * "mf-<variante>-<indice>" no casa con nada y PhotoSwipe no llega a abrirse: se construyen
   * los items a partir del JSON de la galería.
   */
  function buildMetafieldZoomItems(pm, cfg) {
    const slides = Array.from(
      pm.querySelectorAll('.product__media-item[data-media-type="image"]:not(.is-filtered)')
    );
    if (!slides.length) return null;

    const maxSpreadZoom = window.themeVariables?.settings?.mobileZoomFactor || 2;
    const items = [];

    for (const slide of slides) {
      const img = metafieldImageFromSlide(cfg, slide);
      if (!img) return null; // Galería del producto: la resuelve el tema.

      const naturalWidth = img.width || 3000;
      const width = Math.min(naturalWidth, window.innerWidth);
      const srcWidth = Math.ceil(
        Math.min(width * (window.devicePixelRatio || 1) * maxSpreadZoom, naturalWidth, 3000)
      );
      const preview = slide.querySelector('img');

      items.push({
        selected: slide.classList.contains('is-selected'),
        src: imageUrlWithWidth(img.src, srcWidth),
        msrc: preview?.currentSrc || img.src,
        w: width,
        h: Math.round(width / (img.aspectRatio || 1)),
      });
    }

    return items;
  }

  function patchZoomItems(pm, cfg) {
    const zoom = pm.querySelector('product-image-zoom');
    if (!zoom || zoom.dataset.variantGalleryZoomPatched) return;
    zoom.dataset.variantGalleryZoomPatched = 'true';

    const native = typeof zoom._buildItems === 'function' ? zoom._buildItems.bind(zoom) : null;

    if (!native) {
      console.warn(LOG, 'patchZoomItems: product-image-zoom._buildItems no es función');
    }

    zoom._buildItems = async function () {
      const items = buildMetafieldZoomItems(pm, cfg);
      if (items) return items;
      return native ? native() : [];
    };
  }

  function setupOne(pm) {
    if (pm.dataset.variantGallerySetup) return;
    pm.dataset.variantGallerySetup = 'true';

    const sid = pm.dataset.sectionId;
    if (!sid) {
      console.warn(LOG, 'setupOne: falta data-section-id en product-media');
      return;
    }

    const cfg = parseJson(`FeaturedVariantGalleryData-${sid}`);
    if (!cfg || !cfg.variants) {
      console.warn(LOG, 'setupOne: sin cfg o variants', { sid });
      return;
    }

    bindMetafieldPoll(pm, cfg);

    customElements.whenDefined('product-media').then(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          patchOnVariantChanged(pm, cfg);
        });
      });
    });

    customElements.whenDefined('product-image-zoom').then(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          patchZoomItems(pm, cfg);
        });
      });
    });
  }

  function init() {
    const nodes = document.querySelectorAll('product-media[data-variant-metafield-gallery]');
    nodes.forEach(setupOne);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
